import type { JSX } from 'react';

import type { FieldPath, FieldValues, UseFormRegisterReturn } from 'react-hook-form';

import NewSelect from '@/components/ui/NewSelect';
import appIcons from '@/constants/appIcons';
import useI18n from '@/hooks/useI18n';

import useFetchIndustries from '../hooks/useFetchIndustries';
import type { Industry } from '../types/Industry';

type IndustriesDropdownProps<T extends FieldValues> = {
    industry: Industry | null;
    setIndustry: (industry: Industry | null) => void;
    errorMessage?: string;
    label?: string;
    className?: string;
    register?: UseFormRegisterReturn<FieldPath<T>>;
};

export default function IndustriesDropdown<T extends FieldValues>({
    industry,
    setIndustry,
    errorMessage,
    label,
    className,
    register,
}: IndustriesDropdownProps<T>): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { industries, isLoading } = useFetchIndustries({ perPage: 100 });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <NewSelect
            {...register}
            label={label ?? ''}
            placeholder={t('Auth.Business_Industry_Placeholder')}
            prefixIcon={appIcons.outline.business}
            options={industries}
            isLoading={isLoading}
            value={industry}
            optionIdKey="id"
            optionTitleKey="title"
            className={className}
            errorMessage={errorMessage}
            isClearable={false}
            isRequired
            onChange={setIndustry}
        />
    );
}
